import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ChevronRight, LogOut } from 'lucide-react';

const PATH_MAP: Record<string, { title: string; subtitle: string; pageName: string }> = {
    // ── Wedding Admin paths ──
    '/admin/dashboard':      { title: 'Admin Command Center',        subtitle: 'Overview of your wedding planning progress',               pageName: 'Overview'     },
    '/admin/shadi-wala':     { title: 'Shadi Wala AI Assistant',     subtitle: 'AI-powered halls, food, and clothing recommendations',    pageName: 'Shadi Wala'   },
    '/admin/create-post':    { title: 'Broadcast Post',              subtitle: 'Share stories and updates directly with your guests',      pageName: 'Create Post'  },
    '/admin/guests':         { title: 'Guest List Command',          subtitle: 'Track RSVPs, dietary details, and family sides',           pageName: 'Guests'       },
    '/admin/tasks':          { title: 'Timeline & Milestones',       subtitle: 'Track and check off your planning progress',              pageName: 'Tasks'        },
    '/admin/vendors':        { title: 'Vendor Network Hub',          subtitle: 'Manage your shortlisted professionals and bookings',       pageName: 'Vendors'      },
    '/admin/gallery':        { title: 'Shared Digital Album',        subtitle: 'Manage guest-uploaded media and photo streams',            pageName: 'Gallery'      },
    '/admin/photographers':  { title: 'Creative Experts',            subtitle: 'Manage professional photographer upload keys',             pageName: 'Experts'      },
    '/budget':               { title: 'Financial Ledger & Budget',   subtitle: 'Track payments, estimates, and outstanding balances',      pageName: 'Budget'       },
    '/admin/wedding-planner':{ title: 'Mahurat & Astrology',         subtitle: 'Calculate auspicious coordinates and plan structures',     pageName: 'Mahurat'      },
    '/admin/ceremonies':     { title: 'Wedding Ceremonies',          subtitle: 'Organize event locations, dates, and times',              pageName: 'Schedule'     },
    '/admin/menu':           { title: 'Food & Catering Menu',        subtitle: 'Select starters, mains, and desserts for food stalls',     pageName: 'Menu'         },
    '/admin/settings':       { title: 'Admin Control Center',        subtitle: 'Adjust credentials, visual styles, and configuration options', pageName: 'Settings'  },
    // ── Guest paths ──
    '/dashboard':            { title: 'Wedding Feed',                subtitle: 'Exclusive updates and moments from the celebrations',      pageName: 'Feed'         },
    '/guest/tasks':          { title: 'My Tasks',                    subtitle: 'Helpful things assigned to you by the couple',            pageName: 'Tasks'        },
    '/guest/schedule':       { title: 'Wedding Schedule',            subtitle: 'Mark your calendars for these special moments',           pageName: 'Schedule'     },
    '/guest/gallery':        { title: 'Wedding Gallery',             subtitle: 'Capture and relive the most precious memories',           pageName: 'Gallery'      },
    '/guest/menu':           { title: 'Dining Menu',                 subtitle: 'Explore the curated menu for the celebrations',           pageName: 'Menu'         },
    '/guest/food':           { title: 'Food Preferences',            subtitle: 'Share your dietary preferences with the hosts',           pageName: 'Food'         },
    '/guest/vendors':        { title: 'Wedding Vendors',             subtitle: 'Meet the talented team behind this celebration',          pageName: 'Vendors'      },
    '/guest/lost-found':     { title: 'Lost & Found',                subtitle: 'Report or recover lost items from the venue',             pageName: 'Lost & Found' },
};

const AdminHeader: React.FC = () => {
    const location = useLocation();
    const history = useHistory();
    const { user, logout, openProfileModal } = useAuth();

    const isGuest = user?.role === 'guest';
    const defaultPath = isGuest ? '/dashboard' : '/admin/dashboard';
    const currentPath = Object.keys(PATH_MAP).find(path => {
        if (path === '/admin/dashboard' || path === '/dashboard') return location.pathname === path;
        return location.pathname.startsWith(path);
    }) || defaultPath;

    const info = PATH_MAP[currentPath] || PATH_MAP[defaultPath];

    const handleLogout = () => {
        logout();
        history.push('/');
    };

    return (
        <header className="admin-agency-header">
            <div className="admin-agency-header-crumbs">
                <span>{isGuest ? 'Guest Area' : 'Dashboard'}</span>
                <ChevronRight size={10} />
                <span className="active-page">{info.pageName}</span>
            </div>

            <div className="admin-agency-header-main-row">
                <div className="admin-agency-header-title-section">
                    <h1>{info.title}</h1>
                    <p>{info.subtitle}</p>
                </div>

                <div className="admin-agency-header-user-section">
                    <div 
                        className="admin-agency-header-user-card clickable hidden sm:flex"
                        onClick={openProfileModal}
                        title="Click to view full profile"
                        role="button"
                        tabIndex={0}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                                openProfileModal();
                            }
                        }}
                    >
                        <span className="admin-agency-header-user-name">
                            {user?.name || (isGuest ? 'Guest' : 'Wedding Coordinator')}
                        </span>
                        <span className="admin-agency-header-user-badge">
                            {isGuest ? '🎉 Guest' : 'Wedding Admin'}
                        </span>
                    </div>
                    <button
                        onClick={handleLogout}
                        className="admin-agency-header-btn"
                        title="Logout"
                    >
                        <LogOut size={16} />
                    </button>
                </div>
            </div>
        </header>
    );
};

export default AdminHeader;
